'use client';

import { useState } from 'react';
import { BubbleRows } from '@/components/chat/BubbleBlock';
import {
  axisTicks,
  clock,
  weekdayPlural,
  type AvailabilityStripBlock,
} from '@/lib/chat/blocks/availability-strip';

/**
 * Who's free on a day: an hours axis, a bar per person in booked / pencilled /
 * free, and the chosen person's free time in a sentence.
 *
 * Everything on it comes off the block, which the server built from the
 * schedule; nothing here works out a time. Choosing a person is local and
 * reaches nobody, so it works on an older reply too; the go-row opens the
 * schedule on that day, which shows the diary as it is NOW (the footnote says
 * when this one was read).
 */
export function AvailabilityStrip({ block, onOpen }: {
  block: AvailabilityStripBlock;
  live: boolean;
  onAnswer: (text: string) => void;
  onOpen: (path: string) => void;
}) {
  const [picked, setPicked] = useState<string | null>(block.focusUserId);
  const goRow = (
    <BubbleRows
      label={`Open ${block.weekday} on the schedule`}
      rows={[{ id: 'open-day', title: `Open ${block.weekday} on the schedule`, onPress: () => onOpen(block.path) }]}
    />
  );

  if (block.state !== 'open' || !block.window) {
    const text = block.state === 'shut'
      ? `Your business hours have you shut on ${weekdayPlural(block.weekday)}, so there's no free time to show for ${block.dayLabel}.`
      : `Your working day has finished for today. Your business hours end at ${clock(block.hours?.endMin ?? 0)}.`;
    return (
      <div className="mchat-free">
        <p className="mchat-free-note">{text}</p>
        {goRow}
      </div>
    );
  }

  const window = block.window;
  const span = window.endMin - window.startMin;
  const shown = block.people.find(p => p.userId === picked) ?? block.people[0];
  const ticks = axisTicks(window);
  const hoursText = block.hoursSource === 'default'
    ? "Your business hours aren't set, so this uses Monday to Friday, 08:00 to 17:00."
    : `Free time is within your business hours, ${clock(block.hours!.startMin)} to ${clock(block.hours!.endMin)} on ${weekdayPlural(block.weekday)}.`;

  return (
    <div className="mchat-free">
      <div className="mchat-free-chart">
        <div className="mchat-free-axis" aria-hidden="true">
          {ticks.map((t, i) => (
            <span
              key={t.minutes}
              className={`mchat-free-tick${i === 0 ? ' mchat-free-tick--first' : i === ticks.length - 1 ? ' mchat-free-tick--last' : ''}`}
              style={{ left: `${t.at * 100}%` }}
            >
              {i === 0 && block.fromNow ? 'Now' : clock(t.minutes)}
            </span>
          ))}
        </div>
        <div role="group" aria-label={`${block.dayLabel}, ${block.people.length} ${block.people.length === 1 ? 'person' : 'people'}`}>
          {block.people.map(person => {
            const on = person.userId === shown?.userId;
            return (
              <button
                key={person.userId}
                type="button"
                className={`mchat-free-row${on ? ' mchat-free-row--on' : ''}`}
                aria-pressed={on}
                aria-label={`${person.name}: ${person.sentence}`}
                onClick={() => setPicked(person.userId)}
              >
                <span className="mchat-free-who">{person.name}</span>
                <span className="mchat-free-track" aria-hidden="true">
                  {person.segments.map(s => (
                    <span
                      key={`${s.startMin}-${s.kind}`}
                      className={`mchat-free-seg mchat-free-seg--${s.kind}`}
                      style={{ flexGrow: (s.endMin - s.startMin) / span }}
                      data-kind={s.kind}
                    />
                  ))}
                </span>
              </button>
            );
          })}
        </div>
        <div className="mchat-free-legend" aria-hidden="true">
          <span className="mchat-free-key"><span className="mchat-free-swatch mchat-free-seg--booked" />Booked</span>
          <span className="mchat-free-key"><span className="mchat-free-swatch mchat-free-seg--pencilled" />Pencilled</span>
          <span className="mchat-free-key"><span className="mchat-free-swatch mchat-free-seg--free" />Free</span>
        </div>
      </div>

      {shown && (
        <div className="mchat-free-detail" aria-live="polite">
          <span className="mchat-free-detail-who">{shown.isViewer ? 'You' : shown.name} on {block.dayLabel}</span>
          <span className="mchat-free-detail-text">{shown.sentence}</span>
        </div>
      )}

      <p className="mchat-free-foot">
        {block.more > 0 && <>{block.more} more {block.more === 1 ? 'person' : 'people'} with less free time not shown. </>}
        {!!block.unassigned && <>{block.unassigned} job{block.unassigned === 1 ? '' : 's'} that day {block.unassigned === 1 ? 'has' : 'have'} nobody on {block.unassigned === 1 ? 'it' : 'them'} yet. </>}
        {block.ownOnly && <>Only your own diary is shown. </>}
        {hoursText} Mucka doesn&apos;t know each person&apos;s own working hours. Checked at {block.asOf}.
      </p>

      {goRow}
    </div>
  );
}
